import React, { useEffect, useMemo, useState } from "react";
import { productsAPI } from "../services/api";
import { useI18n } from "../i18n";
import Modal from "./Modal";
import { formatMoney } from "../pos/utils/money";
import { productImageUrl } from "../pos/utils/images";

const Thumb = ({ src }) => {
  const [failed, setFailed] = useState(false);
  const url = failed ? null : productImageUrl(src);

  return (
    <span className="shrink-0 w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center overflow-hidden">
      {url ? (
        <img src={url} alt="" loading="lazy" onError={() => setFailed(true)} className="w-full h-full object-cover" />
      ) : (
        "💧"
      )}
    </span>
  );
};

const sameRow = (a, b) => a.sold_by_dose === b.sold_by_dose && Number(a.dose_price || 0) === Number(b.dose_price || 0);

/**
 * Every liquid in one table: tick the ones poured by the dose, give the price of a
 * dose, save once — instead of opening each product to tick its box. A price can be
 * given to every ticked liquid at once. Only the rows that changed are sent.
 */
const DoseSetup = ({ currency, onClose, onDone }) => {
  const { t } = useI18n();
  const [saved, setSaved] = useState(null);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [priceForAll, setPriceForAll] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);

  const take = (list) => {
    const normalised = list.map((row) => ({ ...row, dose_price: row.dose_price ?? "" }));
    setSaved(normalised);
    setRows(normalised);
  };

  useEffect(() => {
    productsAPI
      .doseSetup()
      .then(({ data }) => take(data))
      .catch(() => setMessage({ tone: "error", text: t("doses.setup.failed") }));
  }, [t]);

  const update = (id, patch) =>
    setRows((current) => current.map((row) => (row.id === id ? { ...row, ...patch } : row)));

  const shown = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((row) =>
      [row.name, row.variant_label, row.brand].some((value) => value && value.toLowerCase().includes(term)),
    );
  }, [rows, search]);

  const changed = useMemo(() => {
    if (!saved) return [];
    const before = new Map(saved.map((row) => [row.id, row]));
    return rows.filter((row) => !sameRow(row, before.get(row.id)));
  }, [rows, saved]);

  const missingPrice = rows.filter((row) => row.sold_by_dose && !(Number(row.dose_price) > 0));
  const ticked = rows.filter((row) => row.sold_by_dose).length;

  const applyToTicked = () => {
    if (!(Number(priceForAll) > 0)) return;
    setRows((current) => current.map((row) => (row.sold_by_dose ? { ...row, dose_price: priceForAll } : row)));
  };

  const tickShown = (value) => {
    const ids = new Set(shown.map((row) => row.id));
    setRows((current) => current.map((row) => (ids.has(row.id) ? { ...row, sold_by_dose: value } : row)));
  };

  const save = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const { data } = await productsAPI.saveDoseSetup(
        changed.map((row) => ({
          id: row.id,
          sold_by_dose: row.sold_by_dose,
          dose_price: row.dose_price === "" ? null : Number(row.dose_price),
        })),
      );
      take(data.products);
      setMessage({ tone: "ok", text: t("doses.setup.saved", { count: data.updated }) });
      onDone?.();
    } catch (err) {
      setMessage({ tone: "error", text: err.response?.data?.error || t("doses.setup.failed") });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`💧 ${t("doses.setup.title")}`} onClose={onClose} maxWidthClass="max-w-3xl">
      <p className="text-sm text-gray-600 mb-4">{t("doses.setup.intro")}</p>

      <div className="flex flex-col sm:flex-row gap-2 mb-3">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("doses.setup.search")}
          className="flex-1 border border-gray-300 rounded-lg px-3 py-2"
        />
        <div className="flex gap-2">
          <input
            type="number"
            min="0"
            step="0.1"
            inputMode="decimal"
            dir="ltr"
            value={priceForAll}
            onChange={(e) => setPriceForAll(e.target.value)}
            placeholder={t("doses.setup.priceForAll")}
            className="w-36 border border-gray-300 rounded-lg px-3 py-2"
          />
          <button
            type="button"
            onClick={applyToTicked}
            disabled={!(Number(priceForAll) > 0) || ticked === 0}
            className="shrink-0 rounded-lg border border-primary-600 px-3 py-2 text-sm font-semibold text-primary-700 hover:bg-primary-50 disabled:opacity-40"
          >
            {t("doses.setup.applyToTicked")}
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-2 text-sm">
        <button type="button" onClick={() => tickShown(true)} className="text-primary-700 font-medium hover:underline">
          {t("doses.setup.tickAll")}
        </button>
        <button type="button" onClick={() => tickShown(false)} className="text-gray-600 font-medium hover:underline">
          {t("doses.setup.untickAll")}
        </button>
        <span className="ms-auto text-gray-500">{t("doses.setup.ticked", { count: ticked })}</span>
      </div>

      {saved === null ? (
        <p className="py-8 text-center text-gray-500">{t("common.loading")}</p>
      ) : rows.length === 0 ? (
        <p className="py-8 text-center text-gray-500">{t("doses.setup.empty")}</p>
      ) : (
        <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
          {shown.map((row) => {
            const needsPrice = row.sold_by_dose && !(Number(row.dose_price) > 0);
            return (
              <li
                key={row.id}
                className={`flex items-center gap-3 px-3 py-2 ${row.sold_by_dose ? "bg-primary-50/40" : ""}`}
              >
                <input
                  type="checkbox"
                  checked={row.sold_by_dose}
                  onChange={(e) => update(row.id, { sold_by_dose: e.target.checked })}
                  aria-label={t("doses.product.toggle")}
                  className="w-5 h-5 shrink-0"
                />
                <Thumb src={row.image} />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-gray-900 truncate">
                    {row.name}
                    {row.variant_label ? ` · ${row.variant_label}` : ""}
                  </span>
                  <span className="block text-xs text-gray-500">
                    {t("doses.setup.bottle", { price: formatMoney(row.price, currency) })}
                  </span>
                </span>
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  inputMode="decimal"
                  dir="ltr"
                  value={row.dose_price}
                  disabled={!row.sold_by_dose}
                  onChange={(e) => update(row.id, { dose_price: e.target.value })}
                  placeholder={t("doses.setup.dosePrice")}
                  aria-label={t("doses.product.price")}
                  className={`w-24 shrink-0 border rounded-lg px-2 py-1.5 text-sm disabled:bg-gray-50 disabled:text-gray-400 ${
                    needsPrice ? "border-red-400" : "border-gray-300"
                  }`}
                />
              </li>
            );
          })}
        </ul>
      )}

      {message && (
        <p
          className={`mt-3 rounded-lg p-3 text-sm ${
            message.tone === "error" ? "bg-red-50 text-red-700" : "bg-green-50 text-green-700"
          }`}
        >
          {message.text}
        </p>
      )}

      <div className="sticky bottom-0 bg-white pt-3 mt-3 border-t flex flex-col sm:flex-row sm:items-center gap-2">
        {missingPrice.length > 0 && (
          <span className="text-sm text-red-600">{t("doses.setup.missingPrice", { count: missingPrice.length })}</span>
        )}
        <button
          type="button"
          onClick={save}
          disabled={busy || changed.length === 0 || missingPrice.length > 0}
          className="sm:ms-auto rounded-lg bg-primary-600 px-5 py-3 font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
        >
          {busy
            ? t("doses.setup.saving")
            : changed.length === 0
              ? t("doses.setup.nothing")
              : t("doses.setup.save", { count: changed.length })}
        </button>
      </div>
    </Modal>
  );
};

export default DoseSetup;
